import React from 'react';

// Helper to create predefined option components
const createOptionComponent = (config) => {
  const OptionComponent = (props) => {
    // Pass the config through props so OptionGroup can access it
    return React.createElement('option-config', { 
      ...props,
      optionConfig: config 
    });
  };
  OptionComponent.optionConfig = config; // Keep static property as backup
  OptionComponent.displayName = `Option(${config.value})`; // Help with debugging
  return OptionComponent;
};

// Housing options
export const OptionRenter = createOptionComponent({
  value: 'renter',
  label: 'Renter',
  formatter: () => 'I am a renter',
});

export const OptionHomeowner = createOptionComponent({
  value: 'homeowner',
  label: 'Homeowner',
  formatter: () => 'I am a homeowner',
});

export const OptionSeeking = createOptionComponent({
  value: 'seeking',
  label: 'Seeking housing',
  formatter: () => 'I am currently seeking housing',
});

export const OptionHomeless = createOptionComponent({
  value: 'homeless',
  label: 'Experiencing homelessness',
  formatter: () => 'I am experiencing homelessness',
});

export const OptionNearby = createOptionComponent({
  value: 'nearby',
  label: 'Live near the proposed development',
  formatter: () => 'I live near the proposed development',
});

export const OptionAffected = createOptionComponent({
  value: 'affected',
  label: 'Directly affected by the proposal',
  formatter: () => 'I am directly affected by this proposal',
});

export const OptionConcerned = createOptionComponent({
  value: 'concerned',
  label: 'Concerned community member',
  formatter: () => 'I am a concerned community member',
});

// Priority options
export const OptionAffordability = createOptionComponent({
  value: 'affordability',
  label: 'Housing affordability',
  formatter: () => 'housing affordability',
});

export const OptionGrowth = createOptionComponent({
  value: 'growth',
  label: 'Economic growth',
  formatter: () => 'economic growth',
});

export const OptionInclusiveness = createOptionComponent({
  value: 'inclusiveness',
  label: 'Inclusive communities',
  formatter: () => 'inclusive communities',
});

export const OptionEquity = createOptionComponent({
  value: 'equity',
  label: 'Equity and fairness',
  formatter: () => 'equity and fairness',
});

export const OptionOpportunity = createOptionComponent({
  value: 'opportunity',
  label: 'Economic opportunity',
  formatter: () => 'economic opportunity',
});

export const OptionClimate = createOptionComponent({
  value: 'climate',
  label: 'Climate action',
  formatter: () => 'climate action',
});

export const OptionStability = createOptionComponent({
  value: 'stability',
  label: 'Housing stability',
  formatter: () => 'housing stability',
});

export const OptionWorkforce = createOptionComponent({
  value: 'workforce',
  label: 'Workforce housing',
  formatter: () => 'workforce housing',
});

export const OptionSeniors = createOptionComponent({
  value: 'seniors',
  label: 'Senior housing',
  formatter: () => 'senior housing',
});

export const OptionAccessibility = createOptionComponent({
  value: 'accessibility',
  label: 'Accessible housing',
  formatter: () => 'accessible housing',
});

export const OptionCharacter = createOptionComponent({
  value: 'character',
  label: 'Neighborhood character',
  formatter: () => 'preserving neighborhood character',
});

export const OptionEnvironment = createOptionComponent({
  value: 'environment',
  label: 'Environmental impact',
  formatter: () => 'environmental impact',
});

export const OptionTraffic = createOptionComponent({
  value: 'traffic',
  label: 'Traffic and parking',
  formatter: () => 'traffic and parking concerns',
});

export const OptionInfrastructure = createOptionComponent({
  value: 'infrastructure',
  label: 'Infrastructure capacity',
  formatter: () => 'infrastructure capacity',
});

export const OptionSafety = createOptionComponent({
  value: 'safety',
  label: 'Public safety',
  formatter: () => 'public safety',
});

export const OptionGreenspace = createOptionComponent({
  value: 'greenspace',
  label: 'Preserve green space',
  formatter: () => 'preserving green space',
});

// Family options
export const OptionHasChildren = createOptionComponent({
  value: 'children',
  label: 'Have children',
  formatter: () => 'I have children',
});

export const OptionCareForElderly = createOptionComponent({
  value: 'elderly',
  label: 'Care for elderly family',
  formatter: () => 'I care for elderly family members',
});

export const OptionMultigenerational = createOptionComponent({
  value: 'multigenerational',
  label: 'Multi-generational household',
  formatter: () => 'I live in a multi-generational household',
});

export const OptionPlanningFamily = createOptionComponent({
  value: 'planning',
  label: 'Planning to start a family',
  formatter: () => 'I am planning to start a family',
});

// Custom formatters for specific campaigns
export const OptionEquityHousing = createOptionComponent({
  value: 'equity',
  label: 'Equity and fairness',
  formatter: () => 'racial equity in housing',
});

// Business-specific options
export const OptionSmallBusiness = createOptionComponent({
  value: 'small-business',
  label: 'Small business owner',
  formatter: () => 'I am a small business owner',
});

export const OptionLocalBusiness = createOptionComponent({
  value: 'local-business',
  label: 'Local business owner',
  formatter: () => 'I own a local business in San Francisco',
});

export const OptionEmployees = createOptionComponent({
  value: 'employees',
  label: 'Employ SF residents',
  formatter: () => 'I employ San Francisco residents',
});

export const OptionCustomerBase = createOptionComponent({
  value: 'customer-base',
  label: 'Serve local families',
  formatter: () => 'my business serves local families',
});

export const OptionWorkforceHousing = createOptionComponent({
  value: 'workforce-housing',
  label: 'Need workforce housing',
  formatter: () => 'my employees need affordable housing options',
});

export const OptionEconomicGrowth = createOptionComponent({
  value: 'economic-growth',
  label: 'Support economic growth',
  formatter: () => 'I believe diverse housing supports economic growth',
});

export const OptionCommunitySupport = createOptionComponent({
  value: 'community-support',
  label: 'Community investment',
  formatter: () => 'I am invested in supporting our community',
});

export const OptionBusinessSustainability = createOptionComponent({
  value: 'sustainability',
  label: 'Business sustainability',
  formatter: () => 'housing diversity is important for my business sustainability',
});

// Business types
export const OptionRetail = createOptionComponent({
  value: 'retail',
  label: 'Retail store',
  formatter: () => 'I own a retail store',
});

export const OptionRestaurant = createOptionComponent({
  value: 'restaurant',
  label: 'Restaurant/Food service',
  formatter: () => 'I own a restaurant or food service business',
});

export const OptionProfessionalServices = createOptionComponent({
  value: 'professional',
  label: 'Professional services',
  formatter: () => 'I provide professional services',
});

export const OptionTechnology = createOptionComponent({
  value: 'technology',
  label: 'Technology company',
  formatter: () => 'I run a technology company',
});

export const OptionHealthcare = createOptionComponent({
  value: 'healthcare',
  label: 'Healthcare services',
  formatter: () => 'I provide healthcare services',
});

export const OptionOtherBusiness = createOptionComponent({
  value: 'other-business',
  label: 'Other business type',
  formatter: () => 'I own a business in San Francisco',
});